import React, { createContext, useContext, useState, useRef } from "react";
import { getGTMConfigByBusinessId } from "../services/Handlers/GTMService";
import { useMutation } from "@tanstack/react-query";

const GTMContext = createContext();

export const useGTM = () => {
  const context = useContext(GTMContext);
  if (!context) {
    throw new Error("useGTM must be used within GTMProvider");
  }
  return context;
};

export const GTMProvider = ({ children }) => {
  const [gtmConfigs, setGtmConfigs] = useState(new Map());
  const loadedGTMsRef = useRef(new Set());

  const { mutate: triggerGTMConfig } = useMutation(getGTMConfigByBusinessId, {
    onSuccess: (response, businessId) => {
      const config = response.data?.data?.gtm_config;
      if (
        response.status === 200 &&
        config?.gtm_id &&
        config?.is_active &&
        config?.has_config
      ) {
        const gtmId = config.gtm_id;
        setGtmConfigs(
          (prev) => new Map(prev.set(businessId, { gtmId, isActive: true }))
        );

        if (!window[`gtm_${gtmId}`]) {
          // Load GTM script
          const gtmScript = document.createElement("script");
          gtmScript.async = true;
          gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
          gtmScript.onload = () => {
            window[`gtm_${gtmId}`] = true;
            loadedGTMsRef.current.add(businessId);
          };
          document.head.appendChild(gtmScript);

          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            "gtm.start": new Date().getTime(),
            event: "gtm.js",
            "gtm.uniqueEventId": `${gtmId}_${businessId}_${Date.now()}`,
          });

          if (!document.querySelector(`noscript[data-gtm="${gtmId}"]`)) {
            const noscript = document.createElement("noscript");
            noscript.setAttribute("data-gtm", gtmId);
            const iframe = document.createElement("iframe");
            iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
            iframe.height = "0";
            iframe.width = "0";
            iframe.style.display = "none";
            iframe.style.visibility = "hidden";
            noscript.appendChild(iframe);
            document.body.insertBefore(noscript, document.body.firstChild);
          }
        }
      }
    },
    onError: (error, businessId) => {
      console.error(
        `Failed to load GTM config for business ${businessId}:`,
        error
      );
    },
  });

  const loadGTMForBusiness = (businessId) => {
    if (!businessId || loadedGTMsRef.current.has(businessId)) return;
    triggerGTMConfig(businessId);
  };

  const trackEvent = (eventName, parameters = {}, businessId = null) => {
    const config = businessId ? gtmConfigs.get(businessId) : null;
    if (!config?.gtmId || !config?.isActive) return;
    if (window.dataLayer) {
      window.dataLayer.push({
        event: eventName,
        business_id: businessId,
        gtm_id: config.gtmId,
        timestamp: new Date().toISOString(),
        ...parameters,
      });
    }
  };

  return (
    <GTMContext.Provider
      value={{
        loadGTMForBusiness,
        trackEvent,
        gtmConfigs,
      }}
    >
      {children}
    </GTMContext.Provider>
  );
};
